import styled from '@emotion/styled';
import {
  duration,
  easing,
  palette,
  radius,
  shadow,
  spacing,
  status,
  typography,
  breakpoints,
  zIndex,
} from '../../../../style-utils/theme';
import { shimmer, skeletonBackground } from '../../../../style-utils/motion';
import { ActiveIndicator, m, Staleable } from '../../../../motion';

/**
 * THE SHIFTS TAB — styled layer.
 *
 * NO AURORA ANYWHERE IN THIS FILE, and that is a rule rather than an oversight
 * (design_system.md §10 reserves the gradient for Lobbero IQ). This tab reads
 * attendance records and the raw log; the only model-written thing it can show
 * is the frozen handover, and that arrives already wearing the mark inside
 * <IqHandoffCard>. Putting the gradient on the surface AROUND it would tell a
 * manager that clocking in is an AI feature.
 *
 * Everything is a token. `palette.iris*` reads as "the brand hue" and its values
 * are teal — see the naming note in §2 of the design system.
 */

export const ShiftsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  padding-bottom: ${spacing.xxl};
`;

/* The loaded history — count line, summary, list, pager — dims while the next
   page is in flight instead of falling back to the skeleton. It carries the
   wrap's own column-and-gap because the crossfade wrapper it sits in is a
   plain block. */
export const HistoryStack = styled(Staleable)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

/* ── the Live | History switch ───────────────────────────────────────── */

export const ViewSwitch = styled.div`
  display: inline-flex;
  gap: ${spacing.half};
  padding: ${spacing.half};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.pill};
  align-self: flex-start;
`;

/* THE ACTIVE GROUND IS A SEPARATE, MOVING PILL (ViewTabIndicator, below),
   positioned against this button; the label rides above it. The weight is
   HELD at semibold in both states — the old 500→600 swap reflowed the label
   width on every switch, so the pill it was sliding under changed size. */
export const ViewTab = styled.button<{ active?: boolean }>`
  position: relative;
  border: none;
  cursor: pointer;
  padding: 8px ${spacing.sm};
  /* 44px is the touch floor the design system sets; a segmented control that
     drops below it is unusable on the tablet a manager actually holds. */
  min-height: 44px;
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${p => (p.active ? palette.surface : palette.slate)};
  background: transparent;
  transition: color ${duration.fast} ${easing.standard};

  &:hover:not(:disabled) {
    color: ${p => (p.active ? palette.surface : palette.ink)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ViewTabLabel = styled.span`
  position: relative;
  z-index: 1;
`;

/** The one pill that moves between Live and History. */
export const ViewTabIndicator = styled(ActiveIndicator)`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: ${radius.pill};
  background: ${palette.lagoon};
  box-shadow: ${shadow.xs};
`;

/* ── filters ─────────────────────────────────────────────────────────── */

export const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.sm};
  align-items: end;
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};

  /* minmax(0, 1fr), NEVER a bare 1fr. A bare 1fr track is minmax(auto, 1fr),
     and its auto floor is the widest control's min-content - and a select's
     min-content is its LONGEST OPTION. One long portfolio or building name
     made the phone column 554px wide inside a 390px screen (measured
     2026-09-24), and every filter went off the right edge with it. */
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${palette.slateLight};
`;

const control = `
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  min-height: 44px;
  padding: 8px 16px;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  font-family: ${typography.ui};
  /* 16px, NOT smaller. iOS Safari zooms the whole page when a focused input is
     under 16px, and the zoom is not undone on blur. */
  font-size: ${typography.size.base}px;
  font-weight: 400;
  color: ${palette.ink};
  transition: border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.fast} ${easing.standard};

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const Select = styled.select`
  ${control}
`;

export const DateInput = styled.input`
  ${control}
`;

export const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  min-height: 44px;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  cursor: pointer;
`;

export const ResetButton = styled.button`
  min-height: 44px;
  padding: 8px ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: 500;
  color: ${palette.slate};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* ── the zone caption and the status summary (WP-44) ─────────────────── */

/* Says which zone the DATE filters mean and which zone the TIMES are in.
   Modelled on scope-caption.tsx, which already makes both statements on the
   dashboard; none of it was wired into Shifts, so the filter boundary and the
   rows were computed in different zones with nothing on screen saying so. */
export const ZoneCaption = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: 1.5;
  color: ${palette.slate};
  max-width: 80ch;

  strong {
    color: ${palette.ink};
    font-weight: ${typography.weight.semibold};
  }
`;

export const SummaryBar = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
`;

export const SummaryNote = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

/* ── the list ────────────────────────────────────────────────────────── */

export const ResultLine = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const ShiftList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

/* A BUTTON, not a div with onClick. The whole row opens the detail, so it has to
   be reachable by keyboard and announced as activatable. */
export const ShiftRow = styled.button<{ selected?: boolean }>`
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: ${spacing.sm};
  align-items: center;
  padding: ${spacing.sm} ${spacing.md};
  background: ${p => (p.selected ? palette.irisTintSoft : palette.surface)};
  border: 1px solid ${p => (p.selected ? palette.iris : palette.line)};
  border-radius: ${radius.md};
  transition: background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.fast} ${easing.standard};

  &:hover {
    border-color: ${palette.irisTint};
    background: ${palette.irisTintSoft};
    box-shadow: ${shadow.sm};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${spacing.xs};
  }
`;

export const RowStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const RowPrimary = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: 600;
  color: ${palette.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowSecondary = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowMono = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
  justify-content: flex-end;

  @media (max-width: ${breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;

type Tone = 'brand' | 'neutral' | 'success' | 'warning' | 'alert';

export const Chip = styled.span<{ tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  padding: 4px 8px;
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: 600;
  white-space: nowrap;
  background: ${p => status[p.tone || 'neutral'].bg};
  color: ${p => status[p.tone || 'neutral'].fg};
`;

export const EmptyPanel = styled.div`
  padding: ${spacing.xl} ${spacing.md};
  text-align: center;
  background: ${palette.surface};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.md};
`;

export const EmptyTitle = styled.p`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: 600;
  color: ${palette.inkSoft};
`;

export const EmptyNote = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

/* The shared shimmer, not a local one. Reduced motion is the global rule in
   index.css. */
export const SkeletonRow = styled.div`
  height: 72px;
  border-radius: ${radius.md};
  ${skeletonBackground}
  animation: ${shimmer} 1.4s linear infinite;
`;

export const ErrorPanel = styled.div`
  padding: ${spacing.md};
  border: 1px solid ${status.alert.bg};
  border-left: 3px solid ${status.alert.dot};
  border-radius: ${radius.sm};
  background: ${status.alert.bg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${status.alert.fg};
`;

export const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding-top: ${spacing.xs};
`;

/* ── the detail drawer ───────────────────────────────────────────────── */

/* Both are motion elements: the scrim fades, the drawer slides in from the
   right edge (x + opacity only), and both leave the way they came — the
   variants are spread at the site (shift-detail-drawer.tsx). */
export const Scrim = m.div`
  position: fixed;
  inset: 0;
  background: ${palette.overlay};
  z-index: ${zIndex.overlay};
`;

/* WIDTH IS NEVER ANIMATED. The full-screen toggle switches it in one frame:
   the e2e suite reads the drawer's width synchronously after the press. */
export const Drawer = m.div<{ $fullscreen?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $fullscreen }) => ($fullscreen ? '100%' : 'min(720px, 100%)')};
  z-index: ${zIndex.modal};
  overflow-y: auto;
  /* iOS: the drawer is the scroll container, and momentum scrolling inside a
     fixed element needs this to feel native rather than sticky. */
  -webkit-overflow-scrolling: touch;
  background: ${palette.porcelain};
  box-shadow: ${shadow.xl};
  padding: ${spacing.md};
  /* The safe-area inset, so the close button is not under the home indicator on
     a notched phone in landscape. */
  padding-bottom: calc(${spacing.xxl} + var(--safe-bottom));
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};

  /* THE DRAWER SCROLLS; ITS SECTIONS DO NOT SHRINK. This is a fixed-height
     column flex container, so every Section defaulted to flex-shrink: 1 and a
     long handover compressed the blocks into each other instead of growing the
     scroll — IqCard clips its overflow, so the squeezed handover was CUT, not
     just cramped. Content height is the truth here; the container's own
     overflow-y is the release valve. */
  & > * {
    flex: 0 0 auto;
  }
`;

/* The drawer's body once it has loaded: the same column-and-gap the drawer
   itself lays out, because the sections sit inside the loading crossfade's
   wrapper rather than directly in the drawer. Nothing here clips or shrinks. */
export const DrawerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const DrawerHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

/** The drawer's window controls — expand + close — as one right-edge cluster. */
export const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex: none;
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: 600;
  color: ${palette.ink};
`;

export const DrawerSub = styled.p`
  margin: ${spacing.half} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const CloseButton = styled.button`
  flex: none;
  min-width: 44px;
  min-height: 44px;
  border-radius: ${radius.pill};
  border: 1px solid ${palette.lineStrong};
  background: ${palette.surface};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};

  &:hover {
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const Section = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${palette.slateLight};
`;

export const FactGrid = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${spacing.sm};
`;

export const Fact = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const FactLabel = styled.dt`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

export const FactValue = styled.dd`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: 500;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const CountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(112px, 1fr));
  gap: ${spacing.xs};
`;

export const CountTile = styled.div`
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const CountValue = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: 600;
  color: ${palette.ink};
  font-variant-numeric: tabular-nums;
`;

export const CountLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

export const EntryList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const EntryItem = styled.li<{ tone?: Tone }>`
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
  border-left: 3px solid ${p => status[p.tone || 'neutral'].dot};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const EntryHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
`;

export const EntryTime = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

export const EntryKind = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: 600;
  color: ${palette.ink};
`;

export const EntryBody = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

export const Caveat = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

export const WarnBand = styled.div`
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${status.warning.bg};
  color: ${status.warning.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
`;
